import {
  AdminClientFinanceSchema,
  DOCUMENT_ATTACHMENT_TYPE,
  ClientPaymentResultSchema,
  TierProgressSchema,
  type AdminClientFinance,
  type BiddingModePut,
  type ClientPaymentRequestInput,
  type ClientPaymentResult,
  type KycDocumentRequest,
  type LedgerDirection,
  type TierProgress,
  type UserRolesChange,
  type UserRolesPut,
} from '@carsfinders/contracts';

import { ApiError, apiFetch, buildUrl, fetchWithCsrf } from '../../lib/api';
import {
  type BiddingModeChangeResult,
  type BiddingModeSnapshot,
  type ClientBid,
  type ClientPayment,
  type User,
  type UserDocument,
  type WireBiddingJournalEntry,
  type WireUserRoleJournalEntry,
  type WonCarWithDetails,
} from './types';

/**
 * Clients feature API — every call hits api-node directly (ported from the web
 * RTK Query `usersApi` / `bidLimitApi`). Endpoints consumed:
 * GET /users, GET /users/me, PATCH /users/:id, PATCH /users/:id/password,
 * /users/:id/documents(+/upload,/:docId), GET /users/:id/payments (legacy archive,
 * read-only — its writes answer 410), GET /users/:id/kyc, GET /bids, GET /won-cars,
 * GET|PUT /admin/users/:id/bidding-mode (+ /journal), GET /admin/users/:id/finance,
 * POST /admin/users/:id/payments, POST /admin/users/:id/deposit/movements (adjustment),
 * POST /admin/users/:id/kyc/document-request,
 * PUT /admin/users/:id/roles + GET /admin/users/:id/roles/journal (superadmin only),
 * GET /admin/deals/clients/:userId/tier-progress (volume tiers),
 * DELETE /admin/users/:id/account (account deletion on the client's request).
 */

export function fetchUsers(): Promise<User[]> {
  return apiFetch<User[]>('users');
}

export async function fetchUserById(id: string): Promise<User | undefined> {
  const users = await apiFetch<User[]>('users', { searchParams: { ids: id } });
  return users[0];
}

export function fetchSessionUser(): Promise<User> {
  return apiFetch<User>('users/me');
}

export function updateUser(id: string, data: Partial<User>): Promise<User> {
  return apiFetch<User>(`users/${id}`, { method: 'PATCH', body: data });
}

export function updateUserPassword(args: {
  id: string;
  currentPassword?: string;
  newPassword: string;
}): Promise<void> {
  const { id, currentPassword, newPassword } = args;
  return apiFetch<void>(`users/${id}/password`, {
    method: 'PATCH',
    body: { currentPassword, newPassword },
  });
}

/**
 * «Запросити документ» (KYC defects, 2026-09-30) —
 * POST /admin/users/:id/kyc/document-request. Stores the request on the user
 * and notifies the client (in-app + Telegram). A re-request overwrites the
 * previous one with a fresh timestamp.
 */
export async function requestKycDocument(
  userId: string,
  note?: string,
): Promise<KycDocumentRequest> {
  const response = await apiFetch<{ data: KycDocumentRequest }>(
    `admin/users/${userId}/kyc/document-request`,
    { method: 'POST', body: note ? { note } : {} },
  );
  return response.data;
}

export function fetchUserDocuments(userId: string): Promise<UserDocument[]> {
  return apiFetch<UserDocument[]>(`users/${userId}/documents`);
}

/**
 * Multipart upload — bypasses apiFetch (it only speaks JSON) but still goes
 * through fetchWithCsrf: this POST hits the same CsrfGuard-protected route
 * (user-documents.controller.ts) as the rest of the documents endpoints.
 */
export async function uploadUserDocument(
  userId: string,
  file: File,
  // The API rejects an upload without a type (400). The admin desk attaches
  // files to the client's record — contracts, slips — so it defaults to the
  // KYC-neutral attachment type.
  documentType: string = DOCUMENT_ATTACHMENT_TYPE,
): Promise<{ document: UserDocument }> {
  const formData = new FormData();
  formData.append('documentType', documentType);
  formData.append('file', file);

  const response = await fetchWithCsrf(buildUrl(`users/${userId}/documents/upload`), {
    method: 'POST',
    body: formData,
  });

  const data: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const record = (data ?? {}) as Record<string, unknown>;
    const message =
      (typeof record.error === 'string' && record.error) ||
      (typeof record.message === 'string' && record.message) ||
      `HTTP ${response.status}`;
    throw new ApiError(response.status, message);
  }
  return data as { document: UserDocument };
}

/** Legacy `clientPayments` rows — the tab's read-only «Архів (стара система)». */
export function fetchUserPayments(userId: string): Promise<ClientPayment[]> {
  return apiFetch<ClientPayment[]>(`users/${userId}/payments`);
}

/**
 * «Режим ставок» (admin deals & deposits, phase 9). The mode and every number
 * on the card come from the API; the admin app only previews an UNSAVED choice
 * with the same pure contracts functions the API uses.
 */
export async function fetchBiddingMode(userId: string): Promise<BiddingModeSnapshot> {
  const response = await apiFetch<{ data: BiddingModeSnapshot }>(
    `admin/users/${userId}/bidding-mode`,
  );
  return response.data;
}

/** One transaction server-side: `users.bidding` + journal row + outbox. */
export async function updateBiddingMode(
  userId: string,
  body: BiddingModePut,
): Promise<BiddingModeChangeResult> {
  const response = await apiFetch<{ data: BiddingModeChangeResult }>(
    `admin/users/${userId}/bidding-mode`,
    { method: 'PUT', body },
  );
  return response.data;
}

/** Newest first, at most 50 rows. */
export async function fetchBiddingJournal(userId: string): Promise<WireBiddingJournalEntry[]> {
  const response = await apiFetch<{ data: WireBiddingJournalEntry[] }>(
    `admin/users/${userId}/bidding-mode/journal`,
  );
  return response.data;
}

/* ─────────────────────────── «Ролі» card (superadmin) ─────────────────────────── */

/**
 * The FULL set of managed roles (admin, superadmin, one local_*) the user should
 * hold. Unmanaged roles (user, dealer, …) are kept server-side. One transaction:
 * conditional `users.role` update + journal row.
 */
export async function setUserRoles(
  userId: string,
  body: UserRolesPut,
): Promise<UserRolesChange> {
  const response = await apiFetch<{ data: UserRolesChange }>(`admin/users/${userId}/roles`, {
    method: 'PUT',
    body,
  });
  return response.data;
}

/** Newest first, at most 50 rows. */
export async function fetchUserRolesJournal(userId: string): Promise<WireUserRoleJournalEntry[]> {
  const response = await apiFetch<{ data: WireUserRoleJournalEntry[] }>(
    `admin/users/${userId}/roles/journal`,
  );
  return response.data;
}

/* ─────────────────────── Client «Фінанси» tab (one way in) ─────────────────── */

/** The tab's query key; every payment / adjustment invalidates it. */
export const clientFinanceKey = (userId: string) => ['user', userId, 'finance'] as const;

/**
 * GET /admin/users/:id/finance — the tab's ONE read. PARSED, not cast: JSON has
 * no Date, and the journal / invoices carry several.
 */
export async function fetchClientFinance(userId: string): Promise<AdminClientFinance> {
  const response = await apiFetch<{ data?: unknown }>(
    `admin/users/${encodeURIComponent(userId)}/finance`,
  );
  return AdminClientFinanceSchema.parse(response?.data);
}

/* ─────────────────────── Volume tiers (client tier card) ─────────────────── */

export const clientTierProgressKey = (userId: string) =>
  ['user', userId, 'tier-progress'] as const;

/**
 * GET /admin/deals/clients/:userId/tier-progress — the client's volume-tier
 * progress (`TierProgress`, a bare body). PARSED, not cast: `holdUntil` crosses
 * the wire as an ISO string.
 */
export async function fetchClientTierProgress(userId: string): Promise<TierProgress> {
  const response = await apiFetch<unknown>(
    `admin/deals/clients/${encodeURIComponent(userId)}/tier-progress`,
  );
  return TierProgressSchema.parse(response);
}

/** `ClientPaymentRequestSchema` as sent over the wire: `receivedAt` is an ISO string (the server coerces it). */
export type ClientPaymentBody = Omit<ClientPaymentRequestInput, 'receivedAt'> & {
  receivedAt: string;
};

/**
 * «Додати платіж» — POST /admin/users/:id/payments. One transaction server-side
 * allocates the payment to the deposit and/or invoices. The body's `paymentId`
 * is the idempotency key: resend the SAME body after a failure and the server
 * answers the recorded result (`replayed: true`) instead of paying twice.
 */
export async function recordClientPayment(
  userId: string,
  body: ClientPaymentBody,
): Promise<ClientPaymentResult> {
  const response = await apiFetch<{ data?: unknown }>(
    `admin/users/${encodeURIComponent(userId)}/payments`,
    { method: 'POST', body },
  );
  return ClientPaymentResultSchema.parse(response?.data);
}

/** «Коригування» body — the reason (≤ 500 chars) is optional; when given it is audited. */
export interface DepositAdjustmentInput {
  amountChf: number;
  direction: LedgerDirection;
  reason?: string;
}

/** A deposit correction: ONE ledger row + `admin.deposit_adjusted` audit, one transaction. */
export async function recordDepositAdjustment(
  userId: string,
  body: DepositAdjustmentInput,
): Promise<void> {
  await apiFetch<unknown>(`admin/users/${encodeURIComponent(userId)}/deposit/movements`, {
    method: 'POST',
    body: { kind: 'adjustment', ...body },
  });
}

/**
 * The machine-readable refusal code of a failed call, when the API sent one.
 * api-node's typed envelope is `{success:false, error:{code, message, …}}`;
 * older routes answer a flat `{code}`.
 */
export function apiErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const body = (error.body ?? {}) as Record<string, unknown>;
  const nested = body.error;
  if (nested && typeof nested === 'object') {
    const code = (nested as Record<string, unknown>).code;
    if (typeof code === 'string') return code;
  }
  return typeof body.code === 'string' ? body.code : undefined;
}

/** Why the API refused to delete an account yet (`error.reasons`, legal-pages phase 3). */
export const ACCOUNT_DELETION_BLOCKERS = [
  'OPEN_BIDS',
  'OPEN_DEALS',
  'UNPAID_INVOICES',
  'DEPOSIT_BALANCE',
  'ADMIN_ACCOUNT',
] as const;

export type AccountDeletionBlocker = (typeof ACCOUNT_DELETION_BLOCKERS)[number];

/**
 * DELETE /admin/users/:userId/account — the operator executes a client's
 * emailed deletion request. 200 `{success, data: {userId, deletedAt}}`;
 * 404 USER_NOT_FOUND; 409 ADMIN_ACCOUNT / ACCOUNT_ALREADY_DELETED /
 * ACCOUNT_DELETION_BLOCKED + `error.reasons` (read with {@link deletionBlockers}).
 */
export async function deleteClientAccount(
  userId: string,
): Promise<{ userId: string; deletedAt: string }> {
  const response = await apiFetch<{ data: { userId: string; deletedAt: string } }>(
    `admin/users/${encodeURIComponent(userId)}/account`,
    { method: 'DELETE' },
  );
  return response.data;
}

/** The known blockers a 409 ACCOUNT_DELETION_BLOCKED carried; unknown values dropped. */
export function deletionBlockers(error: unknown): AccountDeletionBlocker[] {
  if (!(error instanceof ApiError)) return [];
  const nested = ((error.body ?? {}) as Record<string, unknown>).error;
  if (!nested || typeof nested !== 'object') return [];
  const reasons = (nested as Record<string, unknown>).reasons;
  if (!Array.isArray(reasons)) return [];
  return reasons.filter((reason): reason is AccountDeletionBlocker =>
    (ACCOUNT_DELETION_BLOCKERS as readonly unknown[]).includes(reason),
  );
}

export interface KycStatus {
  isValid: boolean;
  verificationLevel: string;
  missingDocuments: string[];
  expiredDocuments: string[];
  riskScore: number;
  recommendations: string[];
}

export function fetchKycStatus(userId: string): Promise<KycStatus> {
  return apiFetch<KycStatus>(`users/${userId}/kyc`);
}

export function fetchBids(): Promise<ClientBid[]> {
  return apiFetch<ClientBid[]>('bids');
}

/**
 * GET /won-cars answers an ENVELOPE — `{success, wonCars, total}` — not a bare
 * array, and this function used to assert the array type over it without
 * unwrapping. That lie stayed invisible while the clients screens were the only
 * caller and only read `.filter`… on a value that was never a list.
 *
 * It stopped being invisible once every won-cars consumer shared one React Query
 * key: whichever fetcher resolved first filled the cache for all of them, so a
 * screen that expected a list intermittently got the envelope and died on
 * `wonCars.forEach is not a function`. One key must mean one shape, so this now
 * unwraps exactly like `features/won-cars/api.ts` does.
 */
export async function fetchWonCars(): Promise<WonCarWithDetails[]> {
  const response = await apiFetch<{ wonCars?: WonCarWithDetails[] }>('won-cars');
  return response.wonCars ?? [];
}
